import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import DeleteIcon from '@mui/icons-material/Delete';
import AuthLayout from '../components/AuthLayout';
import clientService, { Client } from '../services/client.service';
import assetService, { Asset } from '../services/asset.service';

const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  
  const [client, setClient] = useState<Client | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [error, setError] = useState('');
  const [assetsError, setAssetsError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalAssets, setTotalAssets] = useState(0);
  
  useEffect(() => {
    fetchClient();
  }, [clientId]);
  
  useEffect(() => {
    if (clientId) {
      fetchClientAssets();
    }
  }, [clientId, page, rowsPerPage]);
  
  const fetchClient = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await clientService.getClientById(clientId as string);
      setClient(response.client);
    } catch (err: any) {
      console.error('Error fetching client:', err);
      setError('Falha ao carregar dados do cliente. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchClientAssets = async () => {
    try {
      setAssetsLoading(true);
      setAssetsError('');
      
      const response = await clientService.getClientAssets(
        clientId as string, 
        page + 1, // API uses 1-based indexing
        rowsPerPage
      );
      
      setAssets(response.assets);
      setTotalAssets(response.pagination.total);
    } catch (err: any) {
      console.error('Error fetching client assets:', err);
      setAssetsError('Falha ao carregar ativos do cliente. Por favor, tente novamente.');
    } finally {
      setAssetsLoading(false);
    }
  };
  
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleBack = () => {
    navigate('/clients');
  };
  
  const handleEditClient = () => {
    navigate(`/clients/edit/${clientId}`);
  };
  
  const handleAddAsset = () => {
    navigate(`/assets/new?clientId=${clientId}`);
  };
  
  const handleViewAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };
  
  const handleEditAsset = (assetId: string) => {
    navigate(`/assets/edit/${assetId}`);
  };
  
  const getClientTypeLabel = (type: string) => {
    switch (type) {
      case 'individual':
        return 'Pessoa Física';
      case 'company':
        return 'Empresa';
      case 'government':
        return 'Governo';
      case 'other':
        return 'Outro';
      default:
        return type;
    }
  };
  
  const getDocumentTypeLabel = (type: string) => {
    return type === 'CPF' ? 'CPF' : 'CNPJ';
  };
  
  if (loading) {
    return (
      <AuthLayout title="Carregando Cliente">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#2e7d32' }} />
        </Box>
      </AuthLayout>
    );
  }
  
  if (error) {
    return (
      <AuthLayout title="Erro">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Voltar para Lista de Clientes
        </Button>
      </AuthLayout>
    );
  }
  
  if (!client) {
    return (
      <AuthLayout title="Cliente não encontrado">
        <Alert severity="warning" sx={{ mb: 3 }}>
          Cliente não encontrado.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Voltar para Lista de Clientes
        </Button>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout title={`Cliente: ${client.name}`}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/clients'); }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography color="text.primary">Clientes</Typography>
          </Link>
          <Typography color="text.primary">
            {client.name}
          </Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            {client.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditClient}
              sx={{ 
                backgroundColor: '#ff9800',
                '&:hover': {
                  backgroundColor: '#f57c00',
                }
              }}
            >
              Editar
            </Button>
          </Box>
        </Box>
        
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Informações Básicas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo de Cliente
                  </Typography>
                  <Typography variant="body1">
                    {getClientTypeLabel(client.clientType)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {getDocumentTypeLabel(client.documentType)}
                  </Typography>
                  <Typography variant="body1">
                    {client.documentNumber}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {client.email || '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Telefone
                  </Typography>
                  <Typography variant="body1">
                    {client.phone || '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={client.isActive ? "Ativo" : "Inativo"} 
                    color={client.isActive ? "success" : "default"}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Endereço
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Logradouro
                  </Typography>
                  <Typography variant="body1">
                    {client.address.street}, {client.address.number}
                    {client.address.complement && ` - ${client.address.complement}`}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Bairro
                  </Typography>
                  <Typography variant="body1">
                    {client.address.neighborhood}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    CEP
                  </Typography>
                  <Typography variant="body1">
                    {client.address.zipCode}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cidade/Estado
                  </Typography>
                  <Typography variant="body1">
                    {client.address.city}/{client.address.state}
                  </Typography>
                </Grid>
                
                {client.address.coordinates && (client.address.coordinates.lat || client.address.coordinates.lng) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Latitude
                      </Typography>
                      <Typography variant="body1">
                        {client.address.coordinates.lat || 'Não informado'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Longitude
                      </Typography>
                      <Typography variant="body1">
                        {client.address.coordinates.lng || 'Não informado'}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            
            {client.contactPerson && (client.contactPerson.name || client.contactPerson.position || client.contactPerson.phone || client.contactPerson.email) && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Pessoa de Contato
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nome
                    </Typography>
                    <Typography variant="body1">
                      {client.contactPerson.name || '-'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cargo
                    </Typography>
                    <Typography variant="body1">
                      {client.contactPerson.position || '-'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Telefone
                    </Typography>
                    <Typography variant="body1">
                      {client.contactPerson.phone || '-'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {client.contactPerson.email || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            
            {client.notes && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Observações
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body1">
                  {client.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Ativos
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddAsset}
            sx={{ 
              backgroundColor: '#2e7d32',
              '&:hover': {
                backgroundColor: '#1b5e20',
              }
            }}
          >
            Novo Ativo
          </Button>
        </Box>
        
        {assetsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {assetsError}
          </Alert>
        )}
        
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table aria-label="assets table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Número de Série</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assetsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={40} sx={{ color: '#2e7d32' }} />
                    </TableCell>
                  </TableRow>
                ) : assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum ativo encontrado para este cliente
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((asset) => (
                    <TableRow hover key={asset._id}>
                      <TableCell>{asset.name}</TableCell>
                      <TableCell>{asset.category}</TableCell>
                      <TableCell>{asset.serialNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={asset.status.charAt(0).toUpperCase() + asset.status.slice(1)} 
                          color={
                            asset.status === 'active' ? 'success' : 
                            asset.status === 'maintenance' ? 'warning' : 
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Visualizar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewAsset(asset._id as string)}
                            sx={{ color: '#2196f3' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditAsset(asset._id as string)}
                            sx={{ color: '#ff9800' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalAssets}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>
    </AuthLayout>
  );
};

export default ClientDetail;
